import React, { useState, useEffect } from 'react';
import getConfig from 'next/config';
import PropTypes from 'prop-types';
import css from 'styled-jsx/css';
import {
  Link,
  GridRow,
  GridCol,
  BodyText,
  FloatingHeader as LmdsFloatingHeader,
} from '@lmig/lmds-react';
import cx from 'classnames';
import Image from '@lmig/dotcom-aspect-components/Utility/Image';
import useLocalStorage from '@lmig/dotcom-aspect-hooks/useLocalStorage';
import Heading from './Utility/Heading';
import CardBase from './Utility/CardBase';

const { className: colClass, styles: colStyles } = css.resolve`
  .message-wrapper {
    margin-top: 16px;
    margin-bottom: 16px;
  }
`;

const { className: rowClass, styles: rowStyles } = css.resolve`
  .dotcom-Splash--innerRow {
    min-height: 600px;
  }
`;

const { className: statueClass, styles: statueStyles } = css.resolve`
  .lady-liberty-container {
    padding-top: 40px;
    align-self: flex-end;
  }
`;

const FourOhFourMessage = ({ statusCode }) => (
  <>
    <Heading type="h2-bold" marginTop="none">
      We couldn't find what you were looking for.
    </Heading>
    <BodyText>
      {statusCode && (
        <pre>
          {`Status code: `}
          <span data-testid="error-status-code">{statusCode}</span>
        </pre>
      )}
      <p />
    </BodyText>
  </>
);

const ErrorDisplay = ({
  err: incomingError = {},
  errorTitle = 'Well, this is unexpected',
  errorMessage = 'LibertyMutual.com is currently unavailable. Thank you for your patience as we work to fix it.',
  statusCode,
}) => {
  const {
    publicRuntimeConfig: { layoutDebugging = false },
  } = getConfig();
  const isDebugging = layoutDebugging === 'true';
  const [debugKey] = useLocalStorage('debug', '', false);
  const [isDebugActive, setIsDebugActive] = useState(isDebugging);
  const error = {
    message: typeof incomingError === 'string' ? incomingError : incomingError.message,
  };
  useEffect(() => {
    setIsDebugActive(isDebugging || !!(debugKey && /^aspect-client/.test(debugKey)));
  }, [debugKey, isDebugging]);

  return (
    <div className="lm-app">
      <LmdsFloatingHeader brand="liberty" brandLinkProps={{ href: '/' }} />

      <main className="dotcom-Splash">
        <GridRow
          className={cx('dotcom-Splash--innerRow', rowClass)}
          mainGrid
          gutters={false}
          justify="start"
          vAlign="middle"
        >
          <GridCol base={12} md={8} lg={9} className={cx('message-wrapper', colClass)}>
            <CardBase shadow padding style={{ borderRadius: '2rem' }}>
              {statusCode === 404 ? (
                <FourOhFourMessage statusCode={statusCode} />
              ) : (
                <>
                  <Heading type="h2-bold">{errorTitle}</Heading>
                  {!isDebugActive && (
                    <>
                      <BodyText>
                        <p>{errorMessage}</p>
                      </BodyText>
                      {/* <BodyText>
                        <p>
                          <strong>
                            {`Looking for an insurance quote? Call `}
                            <Link href="tel:844-669-3663">844-669-3663</Link>
                          </strong>
                        </p>
                      </BodyText> */}
                      <BodyText>
                        <p>
                          <strong>
                            {`Need to access your account or file a claim? `}
                            <Link href="https://eservice.libertymutual.com/account/auth">
                              Log in Here
                            </Link>
                          </strong>
                        </p>
                      </BodyText>
                    </>
                  )}
                  {isDebugActive && error.message && (
                    <CardBase>
                      <div className="errorOverlay">
                        <code>
                          <strong>Error:</strong> {error.message}
                          <br />
                          <strong>Stack: </strong> {error.stack}
                        </code>
                      </div>
                    </CardBase>
                  )}
                </>
              )}
            </CardBase>
            <style>{colStyles}</style>
          </GridCol>
          <GridCol md={1} base={5} />
          <GridCol md={3} lg={2} base={7} className={cx('lady-liberty-container', statueClass)}>
            {/* eslint-disable-next-line jsx-a11y/alt-text */}
            <Image imageDomain="">
              <img
                className="lady-liberty"
                src="/static/images/splash-lady-liberty.png"
                alt="Statue of Liberty"
                height="504"
                width="192"
              />
            </Image>
            <style>{statueStyles}</style>
          </GridCol>
          <style>{rowStyles}</style>
        </GridRow>
      </main>
      <style jsx global>{`
        #__next {
          min-height: 100%;
          height: 100%;
        }

        .lm-Header.lm-Header--container {
          > .lm-Row--main {
            padding: 0 1rem;
          }

          .lm-Masthead {
            .lm-LogoLibertyMutual {
              width: 100%;
            }
          }
        }
      `}</style>
      <style jsx>{`
        .dotcom-Splash {
          background-image: url('/static/images/splash-sky-bg.png');
          background-size: cover;
          height: 0;
          font-size: 20px;

          .lady-liberty {
            max-width: 100%;
            height: auto;
            display: block;
          }
        }
      `}</style>
      <style jsx>{`
        .errorOverlay {
          font-size: 0.764705882em;
          border: solid 1px red;
          padding: 16px;
          background-color: rgba(255, 0, 0, 0.1);
        }
      `}</style>
    </div>
  );
};

ErrorDisplay.propTypes = {
  statusCode: PropTypes.number.isRequired,
  err: PropTypes.shape({
    name: PropTypes.string,
    message: PropTypes.string,
    stack: PropTypes.string,
  }),
  errorTitle: PropTypes.string,
  errorMessage: PropTypes.string,
};

export default ErrorDisplay;
